import { useState } from "react";
import { ListPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export function CreateListFromSelectionDialog({ open, onClose, contactIds, clusters, onDone }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState("static");
  const [clusterIds, setClusterIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setClusterIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const submit = async () => {
    if (!name.trim()) return toast.error("Inserisci il nome della lista");
    setBusy(true);
    try {
      const r = await api.post("/lists/from-selection", { name: name.trim(), description, kind, contact_ids: contactIds, cluster_ids: clusterIds });
      toast.success(`Lista «${r.data.name}» creata: ${r.data.contact_count} contatti`);
      onDone?.(r.data); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-lg" data-testid="create-list-selection-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ListPlus className="h-5 w-5 text-[#10B981]" />Crea lista dai selezionati</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400" data-testid="selection-count-text"><span className="text-slate-100 font-semibold">{contactIds.length}</span> contatti selezionati{clusterIds.length > 0 && <> + tutti i contatti di <span className="text-slate-100 font-semibold">{clusterIds.length}</span> gruppi</>}</p>
        <div className="space-y-3">
          <div><Label>Nome</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#1E293B] border-[#1F2937] text-slate-100" data-testid="list-sel-name" /></div>
          <div><Label>Descrizione</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} className="bg-[#1E293B] border-[#1F2937] text-slate-100" data-testid="list-sel-description" /></div>
          <div>
            <Label>Tipo di lista</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
              {[["static", "Statica", "Fotografia dei contatti selezionati adesso. Non cambia nel tempo."], ["dynamic", "Dinamica", "Segue i gruppi scelti: si aggiorna quando cambiano i membri. I singoli selezionati restano come membri manuali."]].map(([k, t, d]) => (
                <button key={k} type="button" onClick={() => setKind(k)} data-testid={`list-kind-${k}`}
                        className={`text-left rounded-xl border p-3 transition-colors ${kind === k ? "border-[#10B981] bg-[#10B981]/10" : "border-[#1F2937] bg-[#1E293B] hover:border-slate-500"}`}>
                  <p className="text-sm font-medium text-slate-100">{t}</p><p className="text-[11px] text-slate-400 mt-0.5">{d}</p>
                </button>
              ))}
            </div>
          </div>
          {clusters?.length > 0 && (
            <div>
              <Label>Aggiungi tutti i contatti dei gruppi</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {clusters.map((c) => (
                  <button key={c.id} type="button" onClick={() => toggle(c.id)} data-testid={`list-sel-cluster-${c.id}`}
                          className={`text-xs px-3 py-1.5 rounded-full border ${clusterIds.includes(c.id) ? "border-[#10B981] bg-[#10B981]/15 text-[#34D399]" : "border-[#1F2937] text-slate-300 hover:border-slate-600"}`}>{c.name}</button>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="list-sel-cancel">Annulla</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="list-sel-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Crea lista</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
